/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2018, 2022
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'./FallbackRecommender'
], function (
	FallbackRecommender
) {
	'use strict';

	/*
	* In a perfect world we'd be able to cache all our recommender requests. Unfortunately,
	* we can not cache all responses as we do not know what info Smarts is making decisions on.
	* Data may change, mappings may change, etc... . The following are the ones
	* we can cache.
	*/
	const CALL_TYPES_TO_CACHE = Object.freeze({
		ALTERNATE_VISUALIZATIONS: 'Alternate visualizations',
		COMPARE_VISUALIZATIONS: 'Compare visualizations'
	});

	const DEFAULT_ASSET_ID = 'Default Asset Id';
	const MAX_CACHE_ENTRIES = 100;

	return class VisRecommenderDelegator {
		constructor(options) {
			this.fallbackRecommender = new FallbackRecommender(options);
			this.recommender = options.smartsRecommenderWrapper || this.fallbackRecommender;
			this.logger = options.logger;
			this.recommenderCallCaches = {};
		}

		destroy() {
			this.recommenderCallCaches = {};
			this.recommender.destroy();
			this.recommender = null;
		}

		clearRecommenderCache() {
			this.recommenderCallCaches = {};
		}

		/**
		* Get the current cache for the specified call type
		* @param {Enum Value} callType is one of the enum types from CALL_TYPES_TO_CACHE
		* @returns {Object} cache type for the specified call type
		*/
		_getCacheForCallType(callType) {
			if (!this.recommenderCallCaches[callType]) {
				// No current cache of this type so create one and create an index tracker array
				// and a nextIndex object which is used when deciding which key to remove
				// when we hit our cache max limit
				this.recommenderCallCaches[callType] = {
					cacheKeyArray: [], // Allows us to map a key to an index
					nextIndex: 0 // Next index to over write when we go over our cache size limit
				};
			}
			return this.recommenderCallCaches[callType];
		}

		/**
		* Considering column order may be important for recommendations, create a
		* cache key with the columnIds as they are passed and the assest id.
		* @param {Array} columnIds
		* @param {Object} payload containing the assetId
		* @returns {String} key for to represent the column ids and asset id provided
		*/
		_getCacheKeyForColIdsAndPayload(columnIds, payload) {
			const assetId = payload && payload.assetId || DEFAULT_ASSET_ID;
			// Do not directly sort the original array as it messes with the passed in structure
			// Make a shallow copy using slice then sort
			const columnIdShallowCopy = columnIds && columnIds.slice() || [];
			return assetId + columnIdShallowCopy.sort().toString();
		}

		recommendBindingsForNewColumns(visualization, newColumnsIds) {
			return this.recommender.recommendBindingsForNewColumns(visualization, newColumnsIds)
				.catch(function (error) {
					this.logger.error('Smarts Recommender failed. Falling back to classic. See error in arguments.', error);
					return this.fallbackRecommender.recommendBindingsForNewColumns(visualization, newColumnsIds);
				}.bind(this));
		}

		recommendBindingsForSelectedViz(visualization, visId) {
			return this.recommender.recommendBindingsForSelectedViz(visualization, visId)
				.catch(function (error) {
					this.logger.error('Smarts Recommender failed. Falling back to classic. See error in arguments.', error);
					return this.fallbackRecommender.recommendBindingsForSelectedViz(visualization, visId);
				}.bind(this));
		}

		recommendBestAlternateVisualization(visualization, columnIds){
			return this.recommender.recommendBestAlternateVisualization(visualization, columnIds)
				.catch(function (error) {
					this.logger.error('Smarts Recommender failed. Falling back to classic. See error in arguments.', error);
					return this.fallbackRecommender.recommendBestAlternateVisualization(visualization, columnIds);
				}.bind(this));
		}

		_updateACacheWithARecommendation(cache, key, recommendations) {
			const cacheKeyArray = cache.cacheKeyArray;
			/*
			If we are above our cache size limit we should remove the oldest cache key
			This involves finding the key used at the nextIndex for the cache, deleting
			the recommendation for that key in the cache, updating the nextIndex, and
			adding the new recommendation to the cache.

			If we are under the limit then we should just add the recommendation to the
			cache key and update the cacheKeyArray.
			*/
			if (cacheKeyArray.length >= MAX_CACHE_ENTRIES) {
				let nextIndex = cache.nextIndex;
				const keyAtNextIndex = cacheKeyArray[nextIndex];
				delete cache[keyAtNextIndex];
				cacheKeyArray[nextIndex] = key;
				nextIndex += 1;
				cache.nextIndex = nextIndex >= MAX_CACHE_ENTRIES ? 0 : nextIndex;
			} else {
				cacheKeyArray.push(key);
			}
			cache[key] = recommendations;
		}

		recommendAlternateVisualizations(columnIds, payload) {
			const key = this._getCacheKeyForColIdsAndPayload(columnIds, payload);
			let cache = this._getCacheForCallType(CALL_TYPES_TO_CACHE.ALTERNATE_VISUALIZATIONS);
			if (cache[key]) {
				return Promise.resolve(cache[key]);
			} else {
				return this.recommender.recommendAlternateVisualizations(columnIds, payload)
					.then(recommendations => {
						this._updateACacheWithARecommendation(cache, key, recommendations);
						return recommendations;
					})
					.catch(function (error) {
						this.logger.error('Smarts Recommender failed. Falling back to classic. See error in arguments.', error);
						return this.fallbackRecommender.recommendAlternateVisualizations(columnIds, payload);
					}.bind(this));
			}
		}

		recommendRelatedVisualizations(columnIds, payload) {
			return this.recommender.recommendRelatedVisualizations(columnIds, payload)
				.then(recommendations => {
					return recommendations;
				})
				.catch(function (error) {
					this.logger.error('Smarts Recommender failed. Falling back to classic. See error in arguments.', error);
					return this.fallbackRecommender.recommendRelatedVisualizations(columnIds, payload);
				}.bind(this));
		}

		recommendCompareVisualizations(columnIds, payload) {
			const key = this._getCacheKeyForColIdsAndPayload(columnIds, payload);
			let cache = this._getCacheForCallType(CALL_TYPES_TO_CACHE.COMPARE_VISUALIZATIONS);
			if (cache[key]) {
				return Promise.resolve(cache[key]);
			} else {
				return this.recommender.recommendCompareVisualizations(columnIds, payload)
					.then(recommendations => {
						this._updateACacheWithARecommendation(cache, key, recommendations);
						return recommendations;
					})
					.catch(function (error) {
						this.logger.error('Smarts Recommender failed. Falling back to classic. See error in arguments.', error);
						return this.fallbackRecommender.recommendCompareVisualizations(columnIds, payload);
					}.bind(this));
			}
		}

		getRecommendInfo(visualization) {
			if (this.recommender.getRecommendInfo) {
				return this.recommender.getRecommendInfo(visualization);
			} else {
				return Promise.reject('Recommended title not supported');
			}
		}
	};
});
